import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  LocalShipping as ContainerIcon,
} from '@mui/icons-material';
import { api } from '../config/api';

interface LineItem {
  po_number: string;
  line_number: number | null;
  stock_code: string;
  pallet_count: number;
  total_quantity: number;
  unit_type: 'sheets' | 'kg' | 'metres';
  // helpers from PO lines fetch
  description: string;
  ordered_qty: number | null;
}

interface POLine {
  po_number: string;
  line_number: number;
  stock_code: string;
  description: string;
  ordered_qty: number;
  unit: string;
  supplier_code: string;
  chain_of_custody: string | null;
}

interface ManualContainerDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  staffNo: string | undefined;
}

const emptyLine = (): LineItem => ({
  po_number: '',
  line_number: null,
  stock_code: '',
  pallet_count: 1,
  total_quantity: 0,
  unit_type: 'sheets',
  description: '',
  ordered_qty: null,
});

function detectUnitType(stockCode: string, supplierCode?: string): 'sheets' | 'kg' | 'metres' {
  if (supplierCode === 'AVDEUS') return 'metres';
  if (stockCode.includes('X')) return 'sheets';
  return 'kg';
}

export default function ManualContainerDialog({ open, onClose, onSuccess, staffNo }: ManualContainerDialogProps) {
  const [containerNo, setContainerNo] = useState('');
  const [sealNo, setSealNo] = useState('');
  const [lines, setLines] = useState<LineItem[]>([emptyLine()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // PO autocomplete data
  const [poNumbers, setPONumbers] = useState<string[]>([]);
  const [poNumbersLoading, setPONumbersLoading] = useState(false);
  // Cache of PO lines per PO number
  const [poLinesCache, setPOLinesCache] = useState<Record<string, POLine[]>>({});
  const [poLinesLoading, setPOLinesLoading] = useState<Record<string, boolean>>({});

  // Fetch PO numbers on mount
  useEffect(() => {
    if (!open) return;
    setPONumbersLoading(true);
    api.get('/open-orders/po-numbers')
      .then(res => {
        if (res.data.success) {
          setPONumbers(res.data.po_numbers);
        }
      })
      .catch(err => console.error('Failed to fetch PO numbers:', err))
      .finally(() => setPONumbersLoading(false));
  }, [open]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setContainerNo('');
      setSealNo('');
      setLines([emptyLine()]);
      setError(null);
      setSuccessMsg(null);
    }
  }, [open]);

  const fetchPOLines = useCallback(async (poNumber: string) => {
    if (poLinesCache[poNumber] || poLinesLoading[poNumber]) return;
    setPOLinesLoading(prev => ({ ...prev, [poNumber]: true }));
    try {
      const res = await api.get(`/open-orders/lines?po_number=${poNumber}`);
      if (res.data.success) {
        setPOLinesCache(prev => ({ ...prev, [poNumber]: res.data.lines }));
      }
    } catch (err) {
      console.error(`Failed to fetch lines for PO ${poNumber}:`, err);
    } finally {
      setPOLinesLoading(prev => ({ ...prev, [poNumber]: false }));
    }
  }, [poLinesCache, poLinesLoading]);

  const updateLine = (index: number, updates: Partial<LineItem>) => {
    setLines(prev => prev.map((line, i) => i === index ? { ...line, ...updates } : line));
  };

  const handlePOChange = (index: number, newPO: string | null) => {
    const po = newPO || '';
    updateLine(index, { po_number: po, stock_code: '', line_number: null, description: '', ordered_qty: null });
    if (po) fetchPOLines(po);
  };

  const handleStockCodeChange = (index: number, stockCode: string) => {
    const line = lines[index];
    const poLines = poLinesCache[line.po_number] || [];
    const matched = poLines.find(pl => pl.stock_code === stockCode);
    if (matched) {
      const unit = detectUnitType(stockCode, matched.supplier_code);
      updateLine(index, {
        stock_code: stockCode,
        line_number: matched.line_number,
        description: matched.description,
        ordered_qty: matched.ordered_qty,
        unit_type: unit,
      });
    } else {
      updateLine(index, { stock_code: stockCode, line_number: null, description: '', ordered_qty: null });
    }
  };

  const addLine = () => setLines(prev => [...prev, emptyLine()]);

  const removeLine = (index: number) => {
    if (lines.length <= 1) return;
    setLines(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccessMsg(null);

    if (!containerNo.trim()) {
      setError('Container number is required');
      return;
    }

    const validLines = lines.filter(l => l.stock_code && l.pallet_count > 0 && l.total_quantity > 0);
    if (validLines.length === 0) {
      setError('At least one line with stock code, pallets, and quantity is required');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        container_no: containerNo.trim(),
        seal_no: sealNo.trim() || null,
        staff_no: staffNo,
        lines: validLines.map(l => ({
          po_number: l.po_number,
          line_number: l.line_number,
          stock_code: l.stock_code,
          pallet_count: l.pallet_count,
          total_quantity: l.total_quantity,
          unit_type: l.unit_type,
        })),
      };

      const res = await api.post('/containers/manual-create', payload);
      if (res.data.success) {
        setSuccessMsg(res.data.message);
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1500);
      } else {
        setError(res.data.error || 'Unknown error');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create container');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ContainerIcon color="primary" />
        Create Manual Container
        <Chip label="MANUAL" size="small" color="info" sx={{ ml: 1 }} />
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mt: 1, mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {successMsg && <Alert severity="success" sx={{ mt: 1, mb: 2 }}>{successMsg}</Alert>}

        {/* Container Details */}
        <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 3 }}>
          <TextField
            label="Container Number"
            value={containerNo}
            onChange={e => setContainerNo(e.target.value.toUpperCase())}
            required
            size="small"
            placeholder="e.g. ABCD1234567"
            sx={{ flex: 1 }}
          />
          <TextField
            label="Seal Number"
            value={sealNo}
            onChange={e => setSealNo(e.target.value)}
            size="small"
            placeholder="Optional"
            sx={{ flex: 1 }}
          />
        </Box>

        {/* Line Items */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Line Items
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Each row represents a grouped entry (e.g. "10 pallets of stock X, 330,600 sheets total").
          The backend will expand each row into individual pallet records.
        </Typography>

        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, minWidth: 130 }}>PO Number</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Stock Code</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 80 }} align="right">Pallets</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 120 }} align="right">Total Qty</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Unit</TableCell>
                <TableCell sx={{ width: 50 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line, idx) => {
                const availablePOLines = poLinesCache[line.po_number] || [];
                const isLoadingPOLines = poLinesLoading[line.po_number] || false;

                return (
                  <TableRow key={idx}>
                    <TableCell sx={{ py: 1, px: 1 }}>
                      <Autocomplete
                        size="small"
                        options={poNumbers}
                        value={line.po_number || null}
                        onChange={(_, val) => handlePOChange(idx, val)}
                        loading={poNumbersLoading}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="PO #" variant="outlined" size="small" />
                        )}
                        freeSolo
                        onInputChange={(_, val, reason) => {
                          if (reason === 'input') {
                            updateLine(idx, { po_number: val });
                          }
                        }}
                        sx={{ minWidth: 120 }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1, px: 1 }}>
                      {availablePOLines.length > 0 ? (
                        <FormControl size="small" fullWidth>
                          <Select
                            value={line.stock_code}
                            onChange={e => handleStockCodeChange(idx, e.target.value)}
                            displayEmpty
                            size="small"
                          >
                            <MenuItem value="" disabled>
                              {isLoadingPOLines ? 'Loading...' : 'Select stock code'}
                            </MenuItem>
                            {availablePOLines.map(pl => (
                              <MenuItem key={`${pl.stock_code}-${pl.line_number}`} value={pl.stock_code}>
                                {pl.stock_code} — {pl.description}
                              </MenuItem>
                            ))}
                          </Select>
                          {line.description && (
                            <FormHelperText>{line.description}</FormHelperText>
                          )}
                        </FormControl>
                      ) : (
                        <TextField
                          size="small"
                          fullWidth
                          value={line.stock_code}
                          onChange={e => updateLine(idx, { stock_code: e.target.value })}
                          placeholder={isLoadingPOLines ? 'Loading PO lines...' : 'Enter stock code'}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1, px: 1 }} align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={line.pallet_count}
                        onChange={e => updateLine(idx, { pallet_count: Math.max(1, parseInt(e.target.value) || 1) })}
                        inputProps={{ min: 1, style: { textAlign: 'right' } }}
                        sx={{ width: 80 }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1, px: 1 }} align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={line.total_quantity || ''}
                        onChange={e => updateLine(idx, { total_quantity: parseFloat(e.target.value) || 0 })}
                        inputProps={{ min: 0, style: { textAlign: 'right' } }}
                        helperText={line.ordered_qty != null ? `Ordered: ${line.ordered_qty.toLocaleString()}` : undefined}
                        sx={{ width: 140 }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1, px: 1 }}>
                      <Chip
                        label={line.unit_type}
                        size="small"
                        variant="outlined"
                        color={line.unit_type === 'sheets' ? 'primary' : line.unit_type === 'kg' ? 'warning' : 'success'}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1, px: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => removeLine(idx)}
                        disabled={lines.length <= 1}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Button
          startIcon={<AddIcon />}
          onClick={addLine}
          size="small"
          variant="outlined"
        >
          Add Line
        </Button>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !containerNo.trim()}
          startIcon={submitting ? <CircularProgress size={16} /> : <ContainerIcon />}
        >
          {submitting ? 'Creating...' : 'Create Container'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
